/**
 * Number and date formatting per the SBG book §13.1. Every figure on screen
 * goes through these so units, signs, separators and the house timezone (ET)
 * stay consistent. Missing values render as an em dash, never N/A or 0.
 */
import { EASTERN_TIME_ZONE } from './easternTime';

export const MISSING = '—';

const isMissing = (value: unknown): value is null | undefined =>
  value === null || value === undefined || (typeof value === 'number' && !Number.isFinite(value));

const INTEGER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const ONE_DECIMAL = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** 1,284 */
export const formatNumber = (value: number | null | undefined, fractionDigits = 0) => {
  if (isMissing(value)) return MISSING;
  if (fractionDigits === 0) return INTEGER.format(value);
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
};

/** "1,284 leads" / "1 lead" — a count with its unit word. */
export const formatCount = (value: number | null | undefined, singular: string, plural = `${singular}s`) => {
  if (isMissing(value)) return MISSING;
  return `${INTEGER.format(value)} ${value === 1 ? singular : plural}`;
};

/** 85% (whole) or 61.2% (one decimal). Pass a ratio (0.612) with `ratio: true`. */
export const formatPercent = (
  value: number | null | undefined,
  { fractionDigits = 0, ratio = false }: { fractionDigits?: number; ratio?: boolean } = {},
) => {
  if (isMissing(value)) return MISSING;
  const pct = ratio ? value * 100 : value;
  return `${formatNumber(pct, fractionDigits)}%`;
};

/** +12.4% / −3.1% with a true minus; 0.0% for no change. */
export const formatDelta = (value: number | null | undefined, { unit = '%', fractionDigits = 1 } = {}) => {
  if (isMissing(value)) return MISSING;
  const magnitude = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Math.abs(value));
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${magnitude}${unit}`;
};

/** $1,250,000 (no cents) or $1,230.45 with `cents: true`. */
export const formatMoney = (value: number | null | undefined, { cents = false } = {}) => {
  if (isMissing(value)) return MISSING;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  }).format(value);
};

/** $1.25M · $48.5K · $950 — charts and tight tiles only, never tables. */
export const formatMoneyCompact = (value: number | null | undefined) => {
  if (isMissing(value)) return MISSING;
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  if (abs >= 1_000_000) return `${sign}$${trimZeros(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}$${trimZeros(abs / 1_000)}K`;
  return `${sign}$${INTEGER.format(abs)}`;
};

const trimZeros = (n: number) => (n >= 100 ? INTEGER.format(n) : ONE_DECIMAL.format(n).replace(/\.0$/, ''));

/** 3.4 days · 1 day */
export const formatDays = (value: number | null | undefined, fractionDigits = 1) => {
  if (isMissing(value)) return MISSING;
  const rounded = Number(value.toFixed(fractionDigits));
  const text = Number.isInteger(rounded) ? INTEGER.format(rounded) : formatNumber(rounded, fractionDigits);
  return `${text} ${rounded === 1 ? 'day' : 'days'}`;
};

/** 2h 15m · 45m */
export const formatDuration = (minutes: number | null | undefined) => {
  if (isMissing(minutes)) return MISSING;
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

type DateInput = string | number | Date | null | undefined;

/** Parses ISO timestamps and treats bare YYYY-MM-DD as a calendar date (noon UTC, no day shift). */
const toDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(`${value}T12:00:00Z`);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isCalendarDate = (value: DateInput) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
const zoneFor = (value: DateInput) => (isCalendarDate(value) ? 'UTC' : EASTERN_TIME_ZONE);

/** Oct 7, 2026 — or Oct 7 with `short: true` (dense tables, current year). */
export const formatDate = (value: DateInput, { short = false, weekday = false } = {}) => {
  const date = toDate(value);
  if (!date) return MISSING;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: zoneFor(value),
    month: 'short',
    day: 'numeric',
    ...(short ? null : { year: 'numeric' }),
    ...(weekday ? { weekday: 'short' } : null),
  }).format(date);
};

/** Oct 1–7, 2026 · Sep 29 – Oct 3, 2026 · Dec 30, 2026 – Jan 2, 2027 */
export const formatDateRange = (start: DateInput, end: DateInput) => {
  const a = toDate(start);
  const b = toDate(end);
  if (!a && !b) return MISSING;
  if (!a || !b) return formatDate(a ? start : end);
  const zone = zoneFor(start);
  const part = (d: Date, opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-US', { timeZone: zone, ...opts }).format(d);
  const sameYear = part(a, { year: 'numeric' }) === part(b, { year: 'numeric' });
  const sameMonth = sameYear && part(a, { month: 'short' }) === part(b, { month: 'short' });
  const sameDay = sameMonth && part(a, { day: 'numeric' }) === part(b, { day: 'numeric' });
  if (sameDay) return formatDate(start);
  if (sameMonth) return `${part(a, { month: 'short', day: 'numeric' })}–${part(b, { day: 'numeric' })}, ${part(b, { year: 'numeric' })}`;
  if (sameYear) return `${part(a, { month: 'short', day: 'numeric' })} – ${part(b, { month: 'short', day: 'numeric' })}, ${part(b, { year: 'numeric' })}`;
  return `${formatDate(start)} – ${formatDate(end)}`;
};

/** 9:03 AM ET */
export const formatTime = (value: DateInput) => {
  const date = toDate(value);
  if (!date) return MISSING;
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
  return `${time} ET`;
};

/** Oct 7, 2026, 9:03 AM ET */
export const formatDateTime = (value: DateInput) => {
  const date = toDate(value);
  if (!date) return MISSING;
  return `${formatDate(date)}, ${formatTime(date)}`;
};

/** Just now · 12m ago · 2h ago · Yesterday · 3 days ago · then the date after 7 days. */
export const formatRelative = (value: DateInput, now: Date = new Date()) => {
  const date = toDate(value);
  if (!date) return MISSING;
  const diffMs = now.getTime() - date.getTime();
  const future = diffMs < 0;
  const minutes = Math.round(Math.abs(diffMs) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return future ? `In ${minutes}m` : `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return future ? `In ${hours}h` : `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return future ? 'Tomorrow' : 'Yesterday';
  if (days < 7) return future ? `In ${days} days` : `${days} days ago`;
  return formatDate(date);
};

/** (844) 284-2725 */
export const formatPhone = (value: string | null | undefined) => {
  if (!value) return MISSING;
  const digits = value.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return value;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
};

/** First and last initials for an avatar: "Maria Chen" → "MC". */
export const initialsOf = (name: string | null | undefined) => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};
